import { EMPTY, of as observableOf } from 'rxjs';

import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChange,
  ViewEncapsulation,
} from '@angular/core';
import { ConfirmationOrder } from '@models/confirmation-order';
import { OperationError } from '@models/operationError.class';
import { GENERAL_CODE_CALLBACKS, TradeInfo } from '@models/trade-info.class';
import { Store } from '@ngrx/store';
import { catchError } from 'rxjs/operators';
import {
  AppState,
  GEOLOCATION_NOTIFICATION_MESSAGE,
  OPERATION_MODAL_OPENED,
  OPERATION_RESET,
  ORDER_TYPE,
  ORDER_TYPE_LIST,
  TRACKING_EVENTS,
  USER_ROLES,
} from 'src/app';
import {
  NEW_RELIC_KNOWN_ERRORS,
  NewRelicLogger,
} from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import { NotificationService, OperationService, PatronService } from 'src/core';
import {
  AnalyticsTrackingService,
  TrackingOrderType,
} from 'src/core/services/analyticsTracking.service';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'mplvolumenoculto-order',
  templateUrl: './mplVolumenOculto.component.html',
  styleUrls: ['./mplVolumenOculto.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MPLVolumenOcultoComponent implements OnChanges {
  @Input() selectedPrice: number;
  @Input() currentContract: any;
  @Input() currentIssue: any;
  @Input() isReadAndWrite: boolean;
  @Input() hasIssue: boolean;
  @Input() replaceTitles: number;
  @Input() isInternalUser: boolean;
  @Input() userRole: boolean;
  @Input() mfaType: number;
  @Input() readToken: string;
  @Input() elPatronData: any;
  @Input() isDiscretional: any;
  @Input() isGeolocationEnabled: boolean;
  @Input() isPositionMocked: boolean;
  @Input() shouldStartLoaderOnOperationAttempt: boolean;
  @Input() shouldNotifyGeolocationOnOperationAttempt: boolean;

  public confirmationOrder: ConfirmationOrder;
  public isBuyButtonLoading: boolean;
  public isSellButtonLoading: boolean;
  private operationCallback: () => void;

  public minTitles = 2000;
  public titles: number = this.minTitles;
  private maxPrice = 0;

  private puja = 0;
  private partial = 0;
  private order: any = {};

  private duration = 1;

  private saleType: string;
  public showConfirmation = false;
  private lockConfirmation = false;

  private token: string;

  public canOperate = true;

  private lockCancel = false;
  private bitBuy: boolean;

  private tradeInfo: TradeInfo;

  constructor(
    private el: ElementRef,
    private notificationService: NotificationService,
    private operationService: OperationService,
    private store: Store<AppState>,
    private patronService: PatronService,
    private analyticsTrackingService: AnalyticsTrackingService,
    private newRelicLogger: NewRelicLogger,
  ) {}

  reset() {
    this.maxPrice = 0;
    this.puja = 0;
    this.partial = 0;
    this.duration = 1;
    this.titles = this.minTitles;
    this.order = {};
    this.saleType = '';
    this.showConfirmation = false;
    this.token = '';
    this.replaceTitles = 0;
    this.selectedPrice = 0;

    this.store.dispatch({
      type: OPERATION_RESET,
      payload: { reset: true },
    });

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  changeToken(event: any) {
    if (event.length > 6) {
      this.lockConfirmation = true;
      return false;
    } else if (event.length === 6) {
      this.lockConfirmation = false;
    } else {
      this.lockConfirmation = true;
      this.token = event;
    }
  }

  setTitles(event: any) {
    this.titles = event;
    this.isValidTitles();
    this.isOrderReady(true);
  }

  // Determina la parcialidad de acuerdo al porcentaje que se seleccione
  setPartial(event: any) {
    this.partial = event;
    this.partial = Math.floor((this.titles * this.partial) / 100); // se redondea hacia abajo
    this.isValidPrice(false, this.partial, '.parcialidad');
    this.isOrderReady(true);
  }

  setPuja(event: any) {
    this.puja = event;
    this.isValidPrice(false, this.puja, '.puja');
    this.isOrderReady(true);
  }

  setMaxPrice(event: any) {
    const price = Number(event);
    const decimalsToFix = price < 1 ? 3 : 2;
    this.maxPrice = Number(price.toFixed(decimalsToFix));
    this.isValidPrice(false, this.maxPrice, '.prc-max');
    this.isOrderReady(true);
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (
      changes.shouldStartLoaderOnOperationAttempt &&
      !changes.shouldStartLoaderOnOperationAttempt.currentValue &&
      this.operationCallback
    ) {
      /**
       * TODO: This doesn't work yet, due to a bug that re-renders the
       * entire operation puppet after fetching the geolocation, and
       * this wipes out the state of the puppet, so even if
       * the callback gets called, all the variables get their default
       * value right after, and the previous changes are never rendered.
       *
       * This should be replicated on every OrderType once this bug
       * gets fixed.
       */
      this.operationCallback();
      return;
    }

    const { userRole } = changes;

    if (userRole !== undefined && userRole.currentValue !== undefined) {
      this.canOperate = !this.isInternalUser;

      this.canOperate =
        userRole.currentValue === USER_ROLES.READONLY ? false : this.canOperate;
    }

    if (changes.selectedPrice !== undefined) {
      if (changes.selectedPrice.currentValue !== undefined) {
        this.setMaxPrice(changes.selectedPrice.currentValue);
      }
    }

    if (changes.currentIssue !== undefined) {
      if (changes.currentIssue.currentValue !== undefined) {
        this.isOrderReady(true);
      }
    }

    if (changes.replaceTitles !== undefined) {
      if (changes.replaceTitles.currentValue !== undefined) {
        if (changes.replaceTitles.currentValue > 0) {
          this.setTitles(changes.replaceTitles.currentValue);
        }
      }
    }
  }

  isValidTitles(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    const minTit: number = this.minTitles === undefined ? 0 : this.minTitles;
    if (this.titles < minTit || isNaN(this.titles)) {
      return false;
    } else {
      return true;
    }
  }

  isValidPrice(onlyCheck: boolean = false, price: number, searchClass: string) {
    const parent = this.el.nativeElement;
    if (price <= 0 || isNaN(price)) {
      // if (!onlyCheck) {
      //   if (parent.querySelector(searchClass) !== null) {
      //     parent.querySelector(searchClass).classList.add('invalid');
      //   }
      // }
      return false;
    } else {
      // if (!onlyCheck) {
      //   if (parent.querySelector(searchClass) !== null) {
      //     parent.querySelector(searchClass).classList.remove('invalid');
      //   }
      // }
      return true;
    }
  }

  isValidIssue() {
    if (this.currentIssue !== undefined) {
      if (this.currentIssue.issueID !== undefined) {
        return true;
      }
    }
    return false;
  }

  isOrderReady(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;

    if (!this.canOperate) {
      return;
    }

    if (
      !this.isValidTitles(onlyCheck) ||
      !this.isValidPrice(onlyCheck, this.partial, '.parcialidad') ||
      !this.isValidPrice(onlyCheck, this.puja, '.puja') ||
      !this.isValidPrice(onlyCheck, this.maxPrice, '.prc-max') ||
      !this.isValidIssue()
    ) {
      if (
        parent.querySelector('.btn-compra') !== null &&
        parent.querySelector('.btn-venta') !== null
      ) {
        parent.querySelector('.btn-compra').classList.remove('active');
        parent.querySelector('.btn-venta').classList.remove('active');
      }
      return false;
    } else {
      if (parent.querySelector('.btn-compra') !== null) {
        parent.querySelector('.btn-compra').classList.add('active');
      }

      if (this.hasIssue || this.isPositionMocked) {
        parent.querySelector('.btn-venta').classList.add('active');
      }
      return true;
    }
  }

  sendOperativeMessage(titleText: string, messageText: string) {
    let notificationMessage: any = {};

    notificationMessage = {
      message: messageText || 'Necesitas ingresar el token',
      title: titleText || 'Token Necesario',
    };
    this.notificationService.showWarning(notificationMessage);
  }

  buildOrder(orderType: string) {
    const DURATION = 1;
    const CAPITAL_ORDER: number =
      orderType === 'venta'
        ? ORDER_TYPE.VENTAMPLVOLUMENOCULTO
        : ORDER_TYPE.COMPRAMPLVOLUMENOCULTO;

    this.order = {
      contractId: this.currentContract,
      duration: DURATION,
      orders: [
        {
          issueId: new ChangeCharacterFromIssue().ChangeToAmpersand(
            this.currentIssue.issueID,
          ),
          instrumentType: this.currentIssue.instrumentType,
          quantity: this.titles,
          triggerPrice: this.maxPrice,
          pegOffsetValue: this.puja,
          maxFloor: this.partial,
          capitalOrderTypeId: CAPITAL_ORDER,
        },
      ],
    };
  }

  tryBuy() {
    if (this.isOrderReady()) {
      const operationCallback = () => {
        this.buildOrder('compra');
        this.saleType = 'compra';
        this.bitBuy = true;
        this._getChallenge();
      };

      if (this.shouldNotifyGeolocationOnOperationAttempt) {
        this.notificationService.showInfo({
          message: GEOLOCATION_NOTIFICATION_MESSAGE,
        });
        return;
      }

      if (this.shouldStartLoaderOnOperationAttempt) {
        /**
         * TODO: Remove the commented lines when ready to display
         * loading buttons in the operation puppet
         */
        // this.isBuyButtonLoading = true;
        // this.isSellButtonLoading = false;
        this.operationCallback = operationCallback;
        return;
      }

      operationCallback();
    } else {
      this.setShowConfirmation();
    }
  }

  trySell() {
    if (!this.hasIssue && !this.isPositionMocked) {
      return false;
    }
    if (this.isOrderReady()) {
      const operationCallback = () => {
        this.buildOrder('venta');
        this.saleType = 'venta';
        this.bitBuy = false;
        this._getChallenge();
      };

      if (this.shouldNotifyGeolocationOnOperationAttempt) {
        this.notificationService.showInfo({
          message: GEOLOCATION_NOTIFICATION_MESSAGE,
        });
        return;
      }

      if (this.shouldStartLoaderOnOperationAttempt) {
        /**
         * TODO: Remove the commented lines when ready to display
         * loading buttons in the operation puppet
         */
        // this.isBuyButtonLoading = false;
        // this.isSellButtonLoading = true;
        this.operationCallback = operationCallback;
        return;
      }

      operationCallback();
    } else {
      this.setShowConfirmation();
    }
  }

  setShowConfirmation(toShow: boolean = false) {
    this.showConfirmation = toShow;
    this.lockConfirmation = this.isReadAndWrite ? false : true;

    if (!this.showConfirmation) {
      this.token = '';
    } else {
      this.confirmationOrder = {
        issueID: this.order.orders[0].issueId,
        issueName: this.currentIssue.issueName,
        triggerPrice: this.order.orders[0].triggerPrice,
        bid: this.order.orders[0].pegOffsetValue,
        titles: this.order.orders[0].quantity,
        vigency: this.order.duration,
        partiality: this.order.orders[0].maxFloor,
        type: 'MPL Volumen Oculto',
        isBuy: this.bitBuy,
        typeId: ORDER_TYPE_LIST.mplHideVolume,
      };

      this.tradeInfo = new TradeInfo(
        false,
        this.currentIssue,
        TrackingOrderType.MPLHiddenVolume,
        window.location.pathname,
        this.titles.toString(),
        this.saleType,
        (this.maxPrice * this.titles).toString(),
        this.maxPrice.toString(),
        this.order.duration,
      );

      const startTradeInfoData = this.tradeInfo.getAnaliticsTradeInfo();

      this.analyticsTrackingService.logEvent(
        TRACKING_EVENTS.start_trade.event,
        startTradeInfoData,
      );
    }

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  cancelConfirmation() {
    this.showConfirmation = false;
    this.lockConfirmation = false;
    this.token = '';

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  registerOrder() {
    const startTime: any = new Date();
    const hash: any = this.newRelicLogger.buildHash(
      startTime.getTime(),
      this.order.contractId,
      this.currentIssue.issueID,
      this.titles,
      this.order.orders[0].capitalOrderTypeId,
    );

    this.order.orders[0].hash = hash;

    this.operationService
      .registerCapitalOrder(this.order)
      .pipe(
        catchError((error: OperationError) => {
          const customAttributes = {
            _type: 'MplVolumenOculto',
          };

          this.newRelicLogger.setEvent(
            NEW_RELIC_KNOWN_ERRORS.tradeError,
            error,
            customAttributes,
          );

          this.reset();

          this.tradeInfo.setSuccess(false);
          this.tradeInfo.setCallback(error.message);

          const tradeErrorInfoData = this.tradeInfo.getAnaliticsTradeInfo();

          this.analyticsTrackingService.logEvent(
            TRACKING_EVENTS.trade.event,
            tradeErrorInfoData,
          );
          return EMPTY;
        }),
      )
      .subscribe((value: any) => {
        const orderId: number =
          value.electronicOrderId !== undefined && value.electronicOrderId > 0
            ? value.electronicOrderId
            : value.vigenciaId;

        this.reset();
        this.notificationService.showSuccess({
          title: 'Orden Ejecutada',
          message: `La orden ${orderId} fue enviada correctamente.`,
          eventId: undefined,
          isBusinessError: false,
        });

        this.tradeInfo.setSuccess(true);
        this.tradeInfo.setCallback(GENERAL_CODE_CALLBACKS.SUCCESS);
        const tradeSuccesInfoData = this.tradeInfo.getAnaliticsTradeInfo();

        this.analyticsTrackingService.logEvent(
          TRACKING_EVENTS.trade.event,
          tradeSuccesInfoData,
        );
      });
  }

  doMovement() {
    if (this.lockConfirmation) {
      return;
    }
    this.lockCancel = true;
    this.lockConfirmation = true;
    if (!this.isReadAndWrite && this.token.length !== 0) {
      this.updateSession(this.token);
    } else {
      this.registerOrder();
    }
  }

  private _getChallenge() {
    if (this.isReadAndWrite) {
      this.setShowConfirmation(true);
    } else {
      this.patronService
        .getChallenge()
        .pipe(
          catchError((error: any) => {
            this.sendOperativeMessage('Error', error.message);

            this.lockConfirmation = false;
            this.lockCancel = false;
            return observableOf(error);
          }),
        )
        .subscribe((value: any) => {
          this.setShowConfirmation(true);
        });
    }
  }

  /**
   * Update session when token is required
   */
  private updateSession(token: string) {
    this.patronService
      .upgradeToken(
        this.elPatronData.URL_UPGRADE_SESSION,
        this.readToken,
        token,
      )
      .pipe(
        catchError((error: Error) => {
          this.sendOperativeMessage('Token incorrecto', error.message);

          return EMPTY;
        }),
      )
      .subscribe(() => this.registerOrder());
  }
}
